import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

/* Bumped from "theme" to "theme:v2" to reset everyone's saved theme back to
   the new light-water default. Old "theme" value is ignored. */
const STORAGE_KEY = "theme:v2";
const DEFAULT_THEME = "light";

const readInitialTheme = () => {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") return saved;
  } catch (_) {}
  return DEFAULT_THEME;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(readInitialTheme);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {}

    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.style.colorScheme = theme;
    document.body.dataset.theme = theme;

    // Keep <html> + <body> background in sync with the theme. The inline
    // bootstrap in index.html sets these on first paint to avoid a flash;
    // we re-apply on every change so dark→light actually rolls back.
    const pageBg = theme === "dark" ? "#0a1424" : "#eaf4ff";
    root.style.backgroundColor = pageBg;
    document.body.style.backgroundColor = pageBg;

    // Keep the mobile status-bar color in sync with the active theme.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", pageBg);
    const appleMeta = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    );
    if (appleMeta) appleMeta.setAttribute("content", pageBg);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === "dark",
    isLight: theme === "light",
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeProvider;
